/**
 * Cart Recovery Key Metrics Cards
 */
import { motion } from 'framer-motion';
import { ShoppingCart, TrendingUp, DollarSign, ArrowDownRight } from 'lucide-react';

export default function CartRecoveryMetrics({ analytics }) {
  if (!analytics) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="cart-recovery-metrics">
      {/* Abandonment Rate */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-card rounded-xl p-5 border border-border"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">Abandonment Rate</p>
            <p className="text-3xl font-bold text-foreground mt-1">
              {analytics.summary.abandonment_rate}%
            </p>
          </div>
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
            analytics.summary.abandonment_rate > 50 
              ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' 
              : 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
          }`}>
            <ShoppingCart className="w-6 h-6" />
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          {analytics.summary.abandoned_carts} of {analytics.summary.total_carts} carts abandoned
        </p>
      </motion.div>

      {/* Recovery Rate */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-gradient-to-br from-green-500 to-green-600 dark:from-green-600 dark:to-green-700 rounded-xl p-5 text-white"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-green-100 text-sm">Recovery Rate</p>
            <p className="text-3xl font-bold mt-1">
              {analytics.summary.recovery_rate}%
            </p>
          </div>
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
            <TrendingUp className="w-6 h-6" />
          </div>
        </div>
        <p className="text-green-100 text-xs mt-2">
          {analytics.summary.recovered_carts} carts recovered
        </p>
      </motion.div>

      {/* Revenue Recovered */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-card rounded-xl p-5 border border-border"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">Revenue Recovered</p>
            <p className="text-3xl font-bold text-green-600 dark:text-green-400 mt-1">
              ${analytics.revenue.recovered.toLocaleString()}
            </p>
          </div>
          <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
            <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          From abandoned cart recovery
        </p>
      </motion.div>

      {/* Lost Revenue */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-card rounded-xl p-5 border border-border"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">Lost Revenue</p>
            <p className="text-3xl font-bold text-red-500 dark:text-red-400 mt-1">
              ${analytics.revenue.lost.toLocaleString()}
            </p>
          </div>
          <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <ArrowDownRight className="w-6 h-6 text-red-500 dark:text-red-400" />
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          ~${analytics.revenue.potential_recovery.toLocaleString()} recoverable
        </p>
      </motion.div>
    </div>
  );
}
